/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/projects-services.md
 * Marketing Constraints.
 *
 * Change orders are a first-class entity (since 2026-04-09). They live
 * in the change_orders table, NOT as estimates. Do NOT reference the
 * back-compat columns on estimates (change_order_number, is_base_estimate,
 * revision_reason).
 *
 * Lifecycle: DRAFT -> SENT -> APPROVED / REJECTED (4 states, terminal
 * on approved or rejected).
 *
 * XOR scope: each CO links to exactly one project OR one service
 * (CHECK constraint). Per-entity numbering: {SLUG}-CO-{n} for projects,
 * {SLUG}-CA-{n} for services (contract amendment).
 *
 * work_timing field: pre_work (default) or post_work (retroactive).
 *
 * Line items may optionally reference catalog rows but do NOT import
 * from estimates automatically. Approved COs flow into the project
 * or service budget denominator.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  FileSignature,
  GitBranch,
  Clock,
  FileCheck,
  Printer,
  Layers,
  DollarSign,
  Briefcase,
  Repeat,
  ShieldCheck,
  Share2,
  ClipboardList,
  Calculator,
  Hash,
  AlertTriangle,
  MessageSquare,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Change Orders",
  title: "Change orders, first-class.",
  titleHighlight: "Not a spreadsheet bolted on.",
  description:
    "Track scope changes with clear status, approval history, and budget impact so change work does not become margin leakage.",

  featureGrid: {
    title: "A dedicated change order system, not an afterthought",
    items: [
      {
        icon: FileSignature,
        title: "First-Class Entity",
        description:
          "Manage change orders with dedicated records and traceable lifecycle history.",
      },
      {
        icon: GitBranch,
        title: "4-State Lifecycle",
        description:
          "Draft → Sent → Approved or Rejected. Each transition is logged and validated by workflow rules.",
      },
      {
        icon: Hash,
        title: "Per-Entity Numbering",
        description:
          "Project change orders number per job (for example, ACME-CO-1, ACME-CO-2). Service contract amendments use a similar per-service sequence (for example, ACME-CA-1, ACME-CA-2). No global counter — every job has its own sequence.",
      },
      {
        icon: Clock,
        title: "Pre-Work vs Post-Work",
        description:
          "A timing tag tracks whether the change order is for upcoming work or retroactive work. Reports separate the two so you know what's a plan and what's a fix-up.",
      },
      {
        icon: Layers,
        title: "Project or Service Scope",
        description:
          "Keep each change order tied to one clear scope so ownership and budget impact stay unambiguous.",
      },
      {
        icon: FileCheck,
        title: "Line Items With Catalog References",
        description:
          "Add materials, labor, equipment, or free-text lines to a change order. Catalog references are optional — you can add a line that's not in your database when the field calls for it.",
      },
      {
        icon: Printer,
        title: "PDF Export",
        description:
          "Export any change order as a branded PDF in one click. Minimal layout, clean margins, the kind of document you actually want in a customer file.",
      },
      {
        icon: Share2,
        title: "Share Link Approval",
        description:
          "Send a change order to a client via a share link. They review, approve, or reject — no login friction. The approval feeds back into your workflow automatically.",
      },
      {
        icon: DollarSign,
        title: "Budget Rollup on Approval",
        description:
          "Once a change order is approved, its total updates the project or service budget baseline automatically. Variance tracking and billing percentages update without manual cleanup.",
      },
    ],
  },

  workflow: {
    title: "What a change order looks like end to end",
    description:
      "A practical five-step process from scope change to approved financial update.",
    steps: [
      {
        label: "Draft",
        title: "Create the change order on the project or service",
        description:
          "Create a change order from active project or service context, then define scope, timing, and line-item impact.",
        details: [
          "Auto-generated per-entity number on creation",
          "Two-column dialog with live minimal preview",
          "Line items with optional catalog references",
          "Pre-work vs post-work timing tag",
          "Draft edits freely until you send",
        ],
      },
      {
        label: "Send",
        title: "Send to the client with a share link",
        description:
          "Share change order details for client review through low-friction send options and clear status updates.",
        details: [
          "Share link for client approval without login",
          "PDF export via the minimal layout template",
          "Draft → Sent transition with audit log",
          "Sent change orders are locked — no silent edits",
        ],
      },
      {
        label: "Approve",
        title: "Client approves or rejects — you know immediately",
        description:
          "Capture approval outcomes quickly so teams can move forward with confidence or revise when needed.",
        details: [
          "Approve / reject actions on the share link",
          "4-state lifecycle enforced through workflow checks",
          "Client response audited with timestamp",
          "Team notifications on transition",
        ],
      },
      {
        label: "Roll Up",
        title: "Approved COs update the budget baseline",
        description:
          "Apply approved change value to project financial context so downstream reporting and billing reflect current scope.",
        details: [
          "Project budget = base estimate + approved change orders",
          "Service budget derivation follows the same pattern",
          "Progress billing percentages recalculate against the updated budget baseline",
          "Rollup shows approved COs separately from base estimate",
        ],
      },
      {
        label: "Audit",
        title: "Every transition traceable forever",
        description:
          "Use audit history to answer who approved what and when, especially on disputed or retroactive scope changes.",
        details: [
          "Full audit log per change order",
          "Separate reporting for pre-work vs post-work",
          "Who did what when, forever",
          "Customer-facing documentation on request",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: FileSignature,
      title: "A Real Entity, Not a Workaround",
      description:
        "Treat scope change as a structured operational process, not an informal side note on the estimate.",
      bullets: [
        "Dedicated change order records, not estimate flags",
        "Own detail page with tabs for line items, activity, and approval",
        "Workflow-validated lifecycle (Draft → Sent → Approved / Rejected)",
        "Project and service-scoped creation and tracking paths",
        "Own PDF template",
      ],
    },
    {
      icon: Hash,
      title: "Per-Entity Numbering That Actually Makes Sense",
      description:
        "Use job-level numbering that is easier for teams and clients to reference during approvals and billing discussions.",
      bullets: [
        "Project change orders: ACME-CO-1, ACME-CO-2, ...",
        "Service contract amendments: ACME-CA-1, ACME-CA-2, ...",
        "Per-entity sequence, not a global counter",
        "CO vs CA prefix tells you project work from service amendment at a glance",
        "Numbers stay stable even if earlier COs are rejected",
      ],
    },
    {
      icon: Clock,
      title: "Pre-Work vs Post-Work — Because They're Different",
      description:
        "Separate planned changes from retroactive fixes so reporting and operational accountability stay honest.",
      bullets: [
        "Timing tag: upcoming work or retroactive work",
        "Tag visible on every change order list and detail view",
        "Reports separate pre-work from post-work totals",
        "The pre-work vs post-work split gives teams a practical signal of estimating accuracy",
      ],
    },
    {
      icon: DollarSign,
      title: "Approved COs Flow Into the Budget — Automatically",
      description:
        "Update financial context automatically on approved changes so project reporting and billing align with current scope.",
      bullets: [
        "Project budget = base estimate + approved change orders (derived)",
        "Master budget stays static — no silent budget rewrites",
        "Progress billing recalculates against the updated budget baseline automatically",
        "Rollup shows approved COs separately from base estimate",
        "Rejected COs never touch the budget",
      ],
    },
  ],

  connections: {
    title: "How change orders connect to the rest of CC",
    items: [
      {
        icon: Briefcase,
        flow: "Project / Service → Change Orders Tab",
        description:
          "Every project and service has a Change Orders tab under the Costs section. List, create, approve, export — all from the same place you're already working on the job.",
      },
      {
        icon: DollarSign,
        flow: "Approved CO → Budget Rollup",
        description:
          "When a change order is approved, its total flows into the project or service budget baseline automatically. Variance and progress billing update without a manual step.",
      },
      {
        icon: Repeat,
        flow: "Progress Billing → Updated Completion %",
        description:
          "As approved changes increase budget scope, completion percentages recalculate so draws stay accurate after scope changes.",
      },
      {
        icon: MessageSquare,
        flow: "Share Link → Client Approval",
        description:
          "Send a change order link to your client. They approve or reject without a login. The underlying record transitions automatically.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Proposals & Client Portal",
      href: "/features/proposals-portal",
      icon: ClipboardList,
    },
    {
      label: "Team & Permissions",
      href: "/features/collaboration",
      icon: ShieldCheck,
    },
    {
      label: "Cost Database",
      href: "/features/cost-database",
      icon: AlertTriangle,
    },
  ],
};

export function ChangeOrdersPageClient() {
  return <FeaturePageTemplate config={config} />;
}
